import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { notify } from 'react-notify-toast';
import {
  Spinner,
  Row,
  Col,
  Container,
  Card,
  Button,
  Form,
  Table,
  ProgressBar,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChalkboardTeacher,
  faCalendarAlt,
  faUsers,
  faWallet,
  faChartBar,
  faRedoAlt,
  faFileInvoiceDollar,
  faMoneyBillWave,
  faCoins,
  faChevronDown,
  faCheck,
  faExclamationTriangle,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Bar,
  Cell,
} from 'recharts';
import clientAxios from '../../clients/clientAxios';
import LoadingScreen from '../../Layout/LoadingScreen';
import { STUDENT_STATES } from '../../constants';

const currencyFormatter = new Intl.NumberFormat('es-AR', {
  style: 'currency',
  currency: 'ARS',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const monthShortNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const chartPalette = ['#ef4444', '#10b981', '#059669', '#34d399', '#0d9488', '#0891b2', '#0284c7', '#4f46e5', '#6366f1', '#8b5cf6', '#a78bfa', '#047857', '#065f46'];

const maxProjectionMonths = 12;

const recentYearFilterId = 'LAST_12_MONTHS';

const recentYearFilterName = 'Todas las comisiones desde hace 1 año';

const recentYearWindowMonths = 12;

const staleDebtDays = 60;

function formatMonthLabel(monthOffset, anchorMoment) {
  const targetMoment = anchorMoment.clone().add(monthOffset, 'months');
  return `${monthShortNames[targetMoment.month()]} ${String(targetMoment.year()).slice(-2)}`;
}

function parsePaidInstallmentValue(monthlyInstallment, isPartialBill) {
  if (monthlyInstallment === null || monthlyInstallment === undefined) {
    return 0;
  }
  const installmentText = String(monthlyInstallment).trim();
  if (installmentText.length === 0) {
    return 0;
  }
  if (installmentText.length > 2) {
    try {
      const parsedNumbers = JSON.parse(`[${installmentText}]`).map((parsedValue) => Number(parsedValue)).filter((parsedValue) => Number.isFinite(parsedValue));
      if (parsedNumbers.length === 0) {
        return 0;
      }
      const highestNumber = Math.max(...parsedNumbers);
      return isPartialBill ? highestNumber - 1 : highestNumber;
    } catch (parseError) {
      return 0;
    }
  }
  const singleValue = parseFloat(installmentText);
  if (!Number.isFinite(singleValue)) {
    return 0;
  }
  return isPartialBill ? singleValue - 1 : singleValue;
}

function resolveClassroomIndex(studentEntry, classroomName) {
  if (Array.isArray(studentEntry.classroom)) {
    return studentEntry.classroom.indexOf(classroomName);
  }
  return studentEntry.classroom === classroomName ? 0 : -1;
}

function resolveActiveFlag(studentEntry, classroomIndex) {
  if (Array.isArray(studentEntry.isActive)) {
    return classroomIndex !== -1 ? studentEntry.isActive[classroomIndex] : studentEntry.isActive[0];
  }
  return studentEntry.isActive;
}

function resolvePriceSchema(studentEntry, classroomIndex) {
  if (classroomIndex === -1) {
    return null;
  }
  const planList = Array.isArray(studentEntry.plan) ? studentEntry.plan : [];
  const planDataList = Array.isArray(studentEntry.planData) ? studentEntry.planData : [];
  const planReference = planList[classroomIndex];
  if (!planReference) {
    return null;
  }
  const planIdentifier = planReference && typeof planReference === 'object' ? planReference.planId : planReference;
  if (!planIdentifier) {
    return null;
  }
  const matchedPlan = planDataList.find((planEntry) => planEntry && planEntry._id === planIdentifier);
  if (!matchedPlan || !Array.isArray(matchedPlan.priceSchema)) {
    return null;
  }
  const priceSchema = matchedPlan.priceSchema.map((schemaValue) => Number(schemaValue) || 0);
  return priceSchema.length > 0 ? priceSchema : null;
}

function matchesStudentBill(billEntry, studentEntry) {
  if (billEntry.studentId && studentEntry._id && String(billEntry.studentId) === String(studentEntry._id)) {
    return true;
  }
  if (billEntry.email && studentEntry.email && String(billEntry.email).toLowerCase() === String(studentEntry.email).toLowerCase()) {
    return true;
  }
  return false;
}

function matchesClassroomBill(billEntry, classroomName) {
  if (Array.isArray(billEntry.classroom)) {
    return billEntry.classroom.includes(classroomName);
  }
  return billEntry.classroom === classroomName;
}

function buildStudentRow(studentEntry, classroomName, classroomBills, anchorMoment, classroomStartMoment) {
  const classroomIndex = resolveClassroomIndex(studentEntry, classroomName);
  if (resolveActiveFlag(studentEntry, classroomIndex) !== STUDENT_STATES.ACTIVE) {
    return { row: null, missingPlan: false, stale: false };
  }
  const priceSchema = resolvePriceSchema(studentEntry, classroomIndex);
  if (!priceSchema) {
    return { row: null, missingPlan: true, stale: false };
  }
  const planLength = priceSchema.length;
  const studentIdentifier = studentEntry._id || studentEntry.id || studentEntry.email;
  const fullName = `${studentEntry.name || ''} ${studentEntry.surname || ''}`.trim() || studentEntry.email || 'Sin nombre';
  const baseRow = {
    identifier: `${studentIdentifier}__${classroomName}`,
    fullName,
    classroomName,
    planLength,
  };
  const studentBills = classroomBills.filter((billEntry) => matchesStudentBill(billEntry, studentEntry) && matchesClassroomBill(billEntry, classroomName));
  const datedBills = studentBills
    .filter((billEntry) => billEntry.date && moment(billEntry.date).isValid())
    .sort((firstBill, secondBill) => moment(secondBill.date).valueOf() - moment(firstBill.date).valueOf());
  const lastBill = datedBills.length > 0 ? datedBills[0] : null;
  const lastBillLabel = lastBill
    ? `${moment(lastBill.date).format('DD/MM/YY')} · ${currencyFormatter.format(Number(lastBill.amount) || 0)}`
    : 'Sin pagos';
  const hasFullPayment = studentBills.some((billEntry) => billEntry.fullPaid === true);
  const staleCutoff = moment().subtract(staleDebtDays, 'days').startOf('day');
  const lastBillTooOld = lastBill !== null && moment(lastBill.date).isBefore(staleCutoff);
  const neverPaidClassroomTooOld = lastBill === null && !(classroomStartMoment !== null && classroomStartMoment.isAfter(staleCutoff));
  if ((lastBillTooOld || neverPaidClassroomTooOld) && !hasFullPayment) {
    return { row: null, missingPlan: false, stale: true };
  }
  if (hasFullPayment) {
    return {
      row: {
        ...baseRow,
        paidCount: planLength,
        nextLabel: null,
        nextAmount: 0,
        remainingTotal: 0,
        remainingCount: 0,
        scheduleText: 'Pago total',
        startOffset: 0,
        remainingEntries: [],
        lastBillLabel,
      },
      missingPlan: false,
      stale: false,
    };
  }
  const paidValues = studentBills.map((billEntry) => parsePaidInstallmentValue(billEntry.monthlyInstallment, billEntry.partial === true));
  const paidCount = paidValues.length > 0 ? Math.max(0, ...paidValues) : 0;
  if (paidCount >= planLength) {
    return {
      row: {
        ...baseRow,
        paidCount: planLength,
        nextLabel: null,
        nextAmount: 0,
        remainingTotal: 0,
        remainingCount: 0,
        scheduleText: 'Plan completo',
        startOffset: 0,
        remainingEntries: [],
        lastBillLabel,
      },
      missingPlan: false,
      stale: false,
    };
  }
  const lastBillMoment = lastBill ? moment(lastBill.date) : null;
  const paidThisMonth = lastBillMoment !== null && lastBillMoment.isSame(anchorMoment, 'month');
  const previousMonth = anchorMoment.clone().subtract(1, 'months');
  const firstIsOverdue = !paidThisMonth && lastBillMoment !== null && lastBillMoment.isBefore(previousMonth, 'month');
  const startOffset = paidThisMonth ? 1 : 0;
  const remainingEntries = [];
  for (let installmentNumber = paidCount + 1; installmentNumber <= planLength; installmentNumber += 1) {
    const entryPosition = installmentNumber - paidCount - 1;
    remainingEntries.push({
      installmentNumber,
      amount: priceSchema[installmentNumber - 1] || 0,
      monthOffset: startOffset + entryPosition,
      isOverdue: entryPosition === 0 && firstIsOverdue,
      bucketName: entryPosition === 0 && firstIsOverdue ? 'Vencido' : formatMonthLabel(startOffset + entryPosition, anchorMoment),
    });
  }
  const remainingTotal = remainingEntries.reduce((accumulated, remainingEntry) => accumulated + remainingEntry.amount, 0);
  const firstRemaining = remainingEntries[0];
  return {
    row: {
      ...baseRow,
      paidCount,
      nextLabel: firstRemaining.isOverdue ? 'Vencido' : formatMonthLabel(firstRemaining.monthOffset, anchorMoment),
      nextAmount: firstRemaining.amount,
      remainingTotal,
      remainingCount: remainingEntries.length,
      scheduleText: remainingEntries.map((remainingEntry) => `Cuota ${remainingEntry.installmentNumber} (${remainingEntry.isOverdue ? 'Vencido' : formatMonthLabel(remainingEntry.monthOffset, anchorMoment)}): ${currencyFormatter.format(remainingEntry.amount)}`).join(' · '),
      startOffset,
      remainingEntries,
      lastBillLabel,
    },
    missingPlan: false,
    stale: false,
  };
}

function renderProjectionTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) {
    return null;
  }
  const tooltipData = payload[0].payload || {};
  return (
    <div
      style={{
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: '14px 18px',
        boxShadow: '0 12px 28px -4px rgba(0, 0, 0, 0.15), 0 6px 12px -3px rgba(0, 0, 0, 0.08)',
        border: '1px solid #e2e8f0',
        minWidth: '230px',
      }}
    >
      <div className="mb-2 pb-2 border-bottom" style={{ fontWeight: 700, color: '#0f172a', fontSize: '14px' }}>
        {tooltipData.fullLabel || label}
      </div>
      <div className="d-flex align-items-center justify-content-between my-1" style={{ fontSize: '13px' }}>
        <span className="text-muted">A cobrar:</span>
        <span style={{ fontWeight: 700, color: '#059669' }}>
          {currencyFormatter.format(tooltipData.projectedAmount || 0)}
        </span>
      </div>
      <div className="d-flex align-items-center justify-content-between my-1" style={{ fontSize: '13px' }}>
        <span className="text-muted">Cuotas:</span>
        <span style={{ fontWeight: 600, color: '#0f172a' }}>{tooltipData.installmentsCount || 0}</span>
      </div>
      <div className="d-flex align-items-center justify-content-between my-1" style={{ fontSize: '13px' }}>
        <span className="text-muted">Alumnos:</span>
        <span style={{ fontWeight: 600, color: '#0f172a' }}>{tooltipData.studentsCount || 0}</span>
      </div>
    </div>
  );
}

export default function PendingCollections() {
  const [classroomOptions, setClassroomOptions] = useState([]);
  const [classroomSearch, setClassroomSearch] = useState('');
  const [isClassroomOpen, setIsClassroomOpen] = useState(false);
  const [selectedClassroomId, setSelectedClassroomId] = useState('');
  const [projection, setProjection] = useState(null);
  const [selectedBucketName, setSelectedBucketName] = useState(null);
  const [isLoadingClassrooms, setIsLoadingClassrooms] = useState(true);
  const [isLoadingProjection, setIsLoadingProjection] = useState(false);

  useEffect(() => {
    async function fetchClassrooms() {
      try {
        const { data } = await clientAxios.get('/classroom/search?sortBy=-createdAt');
        const classroomEntries = (Array.isArray(data) ? data : [])
          .filter((classroomEntry) => classroomEntry.classroom !== undefined)
          .map((classroomEntry) => ({ identifier: classroomEntry._id, name: classroomEntry.classroom, createdAt: classroomEntry.createdAt, state: classroomEntry.isActive }));
        setClassroomOptions(classroomEntries);
      } catch (requestError) {
        notify.show('No se pudo cargar la lista de comisiones', 'error');
      } finally {
        setIsLoadingClassrooms(false);
      }
    }
    fetchClassrooms();
  }, []);

  async function fetchStudentDetail(studentIdentifier) {
    try {
      const { data } = await clientAxios.get(`/students/${studentIdentifier}`);
      return data && data.response ? data.response : null;
    } catch (requestError) {
      return null;
    }
  }

  async function fetchProjection(classroomIdentifier) {
    const isYearMode = String(classroomIdentifier) === recentYearFilterId;
    const yearCutoff = moment().subtract(recentYearWindowMonths, 'months');
    const targetClassrooms = isYearMode
      ? classroomOptions.filter((optionEntry) => optionEntry.createdAt && moment(optionEntry.createdAt).isSameOrAfter(yearCutoff))
      : classroomOptions.filter((optionEntry) => String(optionEntry.identifier) === String(classroomIdentifier));
    if (targetClassrooms.length === 0) {
      notify.show(isYearMode ? 'No hay comisiones creadas en los últimos 12 meses' : 'Comisión no encontrada', 'warning');
      setIsLoadingProjection(false);
      return;
    }
    setIsLoadingProjection(true);
    try {
      const anchorMoment = moment().startOf('month');
      const classroomResults = await Promise.all(
        targetClassrooms.map(async (targetEntry) => {
          try {
            const [classroomResponse, billsResponse] = await Promise.all([
              clientAxios.get(`/classroom/getStudentsInClassroom/${targetEntry.identifier}`),
              clientAxios.post('/bills/search/', { classroom: targetEntry.name }),
            ]);
            const classroomPayload = classroomResponse.data && classroomResponse.data.data ? classroomResponse.data.data[0] : classroomResponse.data;
            return {
              classroomName: targetEntry.name,
              classroomStart: classroomPayload && classroomPayload.initialDate ? classroomPayload.initialDate : targetEntry.createdAt,
              rawStudents: classroomPayload && Array.isArray(classroomPayload.students) ? classroomPayload.students : [],
              rawBills: Array.isArray(billsResponse.data) ? billsResponse.data : [],
              loadFailed: false,
            };
          } catch (loadError) {
            return { classroomName: targetEntry.name, classroomStart: targetEntry.createdAt, rawStudents: [], rawBills: [], loadFailed: true };
          }
        })
      );
      const failedClassrooms = classroomResults.filter((resultEntry) => resultEntry.loadFailed);
      if (failedClassrooms.length > 0) {
        notify.show(`No se pudo cargar: ${failedClassrooms.map((resultEntry) => resultEntry.classroomName).join(', ')}`, 'error');
      }

      const seenDetailIds = {};
      const studentsNeedingDetail = [];
      classroomResults.forEach((resultEntry) => {
        resultEntry.rawStudents.forEach((studentEntry) => {
          const sourceIdentifier = studentEntry._id || studentEntry.id;
          const needsDetail = !Array.isArray(studentEntry.planData) || studentEntry.planData.length === 0;
          if (sourceIdentifier && needsDetail && !seenDetailIds[String(sourceIdentifier)]) {
            seenDetailIds[String(sourceIdentifier)] = true;
            studentsNeedingDetail.push(studentEntry);
          }
        });
      });
      const detailResponses = await Promise.all(
        studentsNeedingDetail.map((studentEntry) => fetchStudentDetail(studentEntry._id || studentEntry.id))
      );
      const detailByIdentifier = {};
      detailResponses.forEach((detailEntry, detailPosition) => {
        const sourceEntry = studentsNeedingDetail[detailPosition];
        const sourceIdentifier = sourceEntry._id || sourceEntry.id;
        if (detailEntry && sourceIdentifier) {
          detailByIdentifier[String(sourceIdentifier)] = detailEntry;
        }
      });

      const studentRows = [];
      let studentsWithoutPlan = 0;
      let staleStudents = 0;
      let longestRemaining = 0;
      let totalCollected = 0;
      let billsCount = 0;
      classroomResults.forEach((resultEntry) => {
        totalCollected += resultEntry.rawBills.reduce((accumulated, billEntry) => accumulated + (Number(billEntry.amount) || 0), 0);
        billsCount += resultEntry.rawBills.length;
        const enrichedStudents = resultEntry.rawStudents.map((studentEntry) => {
          const sourceIdentifier = studentEntry._id || studentEntry.id;
          const fetchedDetail = sourceIdentifier ? detailByIdentifier[String(sourceIdentifier)] : null;
          return fetchedDetail || studentEntry;
        });
        const classroomStartMoment = resultEntry.classroomStart && moment(resultEntry.classroomStart).isValid()
          ? moment(resultEntry.classroomStart)
          : null;
        enrichedStudents.forEach((studentEntry) => {
          const builtRow = buildStudentRow(studentEntry, resultEntry.classroomName, resultEntry.rawBills, anchorMoment, classroomStartMoment);
          if (builtRow.missingPlan) {
            studentsWithoutPlan += 1;
            return;
          }
          if (builtRow.stale) {
            staleStudents += 1;
            return;
          }
          if (builtRow.row) {
            studentRows.push(builtRow.row);
            if (builtRow.row.remainingEntries.length > longestRemaining) {
              longestRemaining = builtRow.row.remainingEntries.length;
            }
          }
        });
      });

      const horizonSize = Math.min(longestRemaining + 1, maxProjectionMonths);
      const monthlyBuckets = [{
        name: 'Vencido',
        fullLabel: 'Vencido · cobro atrasado',
        projectedAmount: 0,
        installmentsCount: 0,
        studentIds: [],
      }];
      for (let monthOffset = 0; monthOffset < horizonSize; monthOffset += 1) {
        monthlyBuckets.push({
          name: formatMonthLabel(monthOffset, anchorMoment),
          fullLabel: monthOffset === 0 ? `Este mes · ${formatMonthLabel(monthOffset, anchorMoment)}` : formatMonthLabel(monthOffset, anchorMoment),
          projectedAmount: 0,
          installmentsCount: 0,
          studentIds: [],
        });
      }
      studentRows.forEach((studentRow) => {
        studentRow.remainingEntries.forEach((remainingEntry) => {
          const targetBucket = remainingEntry.isOverdue
            ? monthlyBuckets[0]
            : monthlyBuckets[remainingEntry.monthOffset + 1];
          if (targetBucket) {
            targetBucket.projectedAmount += remainingEntry.amount;
            targetBucket.installmentsCount += 1;
            if (!targetBucket.studentIds.includes(studentRow.identifier)) {
              targetBucket.studentIds.push(studentRow.identifier);
            }
          }
        });
      });
      const chartData = monthlyBuckets.map((bucketEntry) => ({
        name: bucketEntry.name,
        fullLabel: bucketEntry.fullLabel,
        projectedAmount: bucketEntry.projectedAmount,
        installmentsCount: bucketEntry.installmentsCount,
        studentsCount: bucketEntry.studentIds.length,
      }));

      const studentsWithRemaining = studentRows.filter((studentRow) => studentRow.remainingCount > 0);
      const studentsUpToDate = studentRows.filter((studentRow) => studentRow.remainingCount === 0);
      const totalProjected = studentsWithRemaining.reduce((accumulated, studentRow) => accumulated + studentRow.remainingTotal, 0);
      const planGrandTotal = totalCollected + totalProjected;
      const currentMonthName = formatMonthLabel(0, anchorMoment);
      const overdueBucket = chartData[0];
      const currentMonthBucket = chartData.find((bucketEntry) => bucketEntry.name === currentMonthName) || { projectedAmount: 0, installmentsCount: 0 };
      setProjection({
        classroomName: isYearMode ? `${targetClassrooms.length} comisiones del último año` : targetClassrooms[0].name,
        classroomCount: targetClassrooms.length,
        isYearMode,
        finishedClassrooms: targetClassrooms.filter((targetEntry) => targetEntry.state === 'done').map((targetEntry) => targetEntry.name),
        chartData,
        currentMonthName,
        overdueAmount: overdueBucket.projectedAmount,
        overdueInstallments: overdueBucket.installmentsCount,
        currentMonthAmount: currentMonthBucket.projectedAmount,
        currentMonthInstallments: currentMonthBucket.installmentsCount,
        studentRows: studentsWithRemaining.sort((firstRow, secondRow) => secondRow.remainingTotal - firstRow.remainingTotal),
        totalProjected,
        totalCollected,
        planGrandTotal,
        collectedPercent: planGrandTotal > 0 ? (totalCollected / planGrandTotal) * 100 : 0,
        billsCount,
        remainingInstallments: studentsWithRemaining.reduce((accumulated, studentRow) => accumulated + studentRow.remainingCount, 0),
        activeStudents: studentRows.length,
        studentsWithRemaining: studentsWithRemaining.length,
        studentsUpToDate: studentsUpToDate.length,
        studentsWithoutPlan,
        staleStudents,
      });
    } catch (requestError) {
      notify.show(requestError.response?.data?.message || 'Error al proyectar pendientes de cobro', 'error');
    } finally {
      setIsLoadingProjection(false);
    }
  }

  useEffect(() => {
    if (selectedClassroomId) {
      fetchProjection(selectedClassroomId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClassroomId]);

  const recentYearOption = { identifier: recentYearFilterId, name: recentYearFilterName, createdAt: null };

  const normalizedSearch = classroomSearch.trim().toLowerCase();

  const filteredClassrooms = normalizedSearch === ''
    ? classroomOptions
    : classroomOptions.filter((optionEntry) => optionEntry.name.toLowerCase().includes(normalizedSearch));

  const visibleClassroomOptions = normalizedSearch === '' || recentYearFilterName.toLowerCase().includes(normalizedSearch)
    ? [recentYearOption, ...filteredClassrooms]
    : filteredClassrooms;

  function handleClassroomSearch(event) {
    const searchValue = event.target.value;
    setClassroomSearch(searchValue);
    setIsClassroomOpen(true);
    if (searchValue === '') {
      setSelectedClassroomId('');
      setProjection(null);
      setSelectedBucketName(null);
    }
  }

  function selectClassroomOption(optionEntry) {
    setSelectedClassroomId(optionEntry.identifier);
    setClassroomSearch(optionEntry.name);
    setProjection(null);
    setSelectedBucketName(null);
    setIsClassroomOpen(false);
  }

  function handleBarClick(clickState) {
    const clickedLabel = clickState && clickState.activeLabel ? clickState.activeLabel : null;
    if (!clickedLabel) {
      return;
    }
    setSelectedBucketName((previousBucket) => (previousBucket === clickedLabel ? null : clickedLabel));
  }

  function buildBucketDetail(bucketName) {
    if (!projection || !bucketName) {
      return null;
    }
    const classroomGroups = {};
    const bucketStudents = [];
    projection.studentRows.forEach((studentRow) => {
      const bucketEntries = studentRow.remainingEntries.filter((remainingEntry) => remainingEntry.bucketName === bucketName);
      if (bucketEntries.length === 0) {
        return;
      }
      const studentTotal = bucketEntries.reduce((accumulated, bucketEntry) => accumulated + bucketEntry.amount, 0);
      bucketStudents.push({
        identifier: studentRow.identifier,
        fullName: studentRow.fullName,
        classroomName: studentRow.classroomName,
        installmentsText: bucketEntries.map((bucketEntry) => `Cuota ${bucketEntry.installmentNumber}`).join(', '),
        isOverdue: bucketEntries.some((bucketEntry) => bucketEntry.isOverdue),
        studentTotal,
      });
      const groupKey = studentRow.classroomName;
      if (!classroomGroups[groupKey]) {
        classroomGroups[groupKey] = { classroomName: groupKey, amount: 0, installmentsCount: 0, studentIds: [] };
      }
      classroomGroups[groupKey].amount += studentTotal;
      classroomGroups[groupKey].installmentsCount += bucketEntries.length;
      if (!classroomGroups[groupKey].studentIds.includes(studentRow.identifier)) {
        classroomGroups[groupKey].studentIds.push(studentRow.identifier);
      }
    });
    return {
      classroomRows: Object.values(classroomGroups)
        .map((groupEntry) => ({ ...groupEntry, studentsCount: groupEntry.studentIds.length }))
        .sort((firstGroup, secondGroup) => secondGroup.amount - firstGroup.amount),
      bucketStudents: bucketStudents.sort((firstStudent, secondStudent) => secondStudent.studentTotal - firstStudent.studentTotal),
    };
  }

  const selectedBucketDetail = buildBucketDetail(selectedBucketName);

  function handleClassroomKeyDown(event) {
    if (event.key === 'Escape') {
      setIsClassroomOpen(false);
      return;
    }
    if (event.key === 'Enter' && visibleClassroomOptions.length > 0) {
      event.preventDefault();
      selectClassroomOption(visibleClassroomOptions[0]);
    }
  }

  if (isLoadingClassrooms) {
    return <LoadingScreen />;
  }

  return (
    <Container fluid className="px-0 mb-4">
      <Card className="border-0 shadow-sm rounded-lg bg-white" style={{ overflow: 'visible' }}>
        <div className="bg-light rounded-top px-4 py-3 border-bottom d-flex flex-column flex-sm-row justify-content-between align-items-sm-center">
          <div className="d-flex align-items-center mb-2 mb-sm-0">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center mr-2"
              style={{ width: '34px', height: '34px', backgroundColor: '#FEF3C7', color: '#D97706' }}
            >
              <FontAwesomeIcon icon={faChalkboardTeacher} size="sm" />
            </div>
            <div>
              <h6 className="font-weight-bold text-dark mb-0">Proyección por comisión</h6>
              <small className="text-muted">Cuotas restantes de alumnos activos según su plan de pagos</small>
            </div>
          </div>
          <div className="d-flex align-items-center">
            <Button
              variant="warning"
              size="sm"
              className="d-flex align-items-center font-weight-bold shadow-sm"
              onClick={() => fetchProjection(selectedClassroomId)}
              disabled={isLoadingProjection || !selectedClassroomId}
            >
              <FontAwesomeIcon icon={faRedoAlt} className="mr-1" />
              {isLoadingProjection ? 'Calculando...' : 'Actualizar'}
            </Button>
          </div>
        </div>
        <Card.Body className="p-4">
          <Form>
            <Row>
              <Col xs={12} md={6}>
                <Form.Group className="mb-0">
                  <Form.Label className="text-muted small text-uppercase font-weight-bold d-flex align-items-center mb-1">
                    <FontAwesomeIcon icon={faChalkboardTeacher} className="mr-1 text-primary" />
                    Comisión
                  </Form.Label>
                  <div style={{ position: 'relative' }}>
                    <div className="d-flex">
                      <Form.Control
                        type="text"
                        value={classroomSearch}
                        onChange={handleClassroomSearch}
                        onFocus={() => setIsClassroomOpen(true)}
                        onKeyDown={handleClassroomKeyDown}
                        autoComplete="off"
                        placeholder="Tocá para ver todo o escribí para filtrar..."
                        className="rounded"
                      />
                      <Button
                        variant="outline-secondary"
                        className="ml-2 flex-shrink-0"
                        onClick={() => setIsClassroomOpen((previousOpen) => !previousOpen)}
                      >
                        <FontAwesomeIcon icon={faChevronDown} />
                      </Button>
                    </div>
                    {isClassroomOpen && (
                      <>
                        <div onClick={() => setIsClassroomOpen(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000 }} />
                        <div
                          className="list-group bg-white border rounded"
                          style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '4px', maxHeight: '280px', overflowY: 'auto', zIndex: 1001, boxShadow: '0 12px 28px -4px rgba(0, 0, 0, 0.15)' }}
                        >
                          {visibleClassroomOptions.length === 0 ? (
                            <div className="list-group-item text-muted small">Sin resultados para esa búsqueda</div>
                          ) : (
                            visibleClassroomOptions.map((optionEntry) => {
                              const isSelected = String(optionEntry.identifier) === String(selectedClassroomId);
                              const isYearOption = String(optionEntry.identifier) === recentYearFilterId;
                              return (
                                <button
                                  key={optionEntry.identifier}
                                  type="button"
                                  onClick={() => selectClassroomOption(optionEntry)}
                                  className={`list-group-item list-group-item-action d-flex align-items-center justify-content-between ${isSelected ? 'active' : ''} ${isYearOption ? 'font-weight-bold' : ''}`}
                                >
                                  <span>{optionEntry.name}</span>
                                  {isSelected && <FontAwesomeIcon icon={faCheck} size="sm" />}
                                </button>
                              );
                            })
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </Form.Group>
              </Col>
              <Col xs={12} md={6} className="d-flex align-items-center mt-3 mt-md-4">
                <small className="text-muted font-italic">
                  La cuota más atrasada de cada deudor figura como Vencido; el resto se proyecta mes a mes. Si pagó este mes, todo va desde el próximo.
                </small>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {isLoadingProjection ? (
        <Card className="border-0 shadow-sm rounded-lg bg-white p-5 text-center mt-3">
          <div className="d-flex justify-content-center align-items-center py-4">
            <Spinner animation="border" role="status" size="md" className="mr-3" variant="warning" />
            <span className="text-muted font-weight-bold">Proyectando cuotas restantes...</span>
          </div>
        </Card>
      ) : !projection ? (
        <Card className="border-0 shadow-sm rounded-lg bg-white p-5 text-center mt-3">
          <div className="text-muted py-4">
            <FontAwesomeIcon icon={faCalendarAlt} size="2x" className="mb-3 text-secondary" style={{ opacity: 0.5 }} />
            <p className="font-weight-bold mb-1 text-dark">Seleccioná una comisión</p>
            <small className="text-muted">Elegí una comisión para ver cuánto queda por cobrar mes a mes.</small>
          </div>
        </Card>
      ) : projection.activeStudents === 0 ? (
        <Card className="border-0 shadow-sm rounded-lg bg-white p-5 text-center mt-3">
          <div className="text-muted py-4">
            <FontAwesomeIcon icon={faUsers} size="2x" className="mb-3 text-secondary" style={{ opacity: 0.5 }} />
            <p className="font-weight-bold mb-1 text-dark">Sin alumnos activos</p>
            <small className="text-muted">La comisión {projection.classroomName} no tiene alumnos activos.</small>
          </div>
        </Card>
      ) : (
        <>
          <Row className="mt-3 mb-2">
            <Col lg={4} sm={6} className="mb-3">
              <Card className="border-0 shadow-sm rounded-lg h-100 bg-white">
                <Card.Body className="d-flex flex-column justify-content-between p-3">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="text-muted small text-uppercase font-weight-bold text-truncate mr-2">
                      Cobrado hasta ahora
                    </span>
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{ width: '38px', height: '38px', backgroundColor: '#ECFDF5', color: '#059669' }}
                    >
                      <FontAwesomeIcon icon={faMoneyBillWave} />
                    </div>
                  </div>
                  <div>
                    <div className="font-weight-bold mb-1 text-dark text-truncate" style={{ fontSize: '1.28rem', letterSpacing: '-0.02em' }}>
                      {currencyFormatter.format(projection.totalCollected)}
                    </div>
                    <small className="text-muted d-block text-truncate">
                      {projection.billsCount} cobros registrados en {projection.classroomName}
                    </small>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={4} sm={6} className="mb-3">
              <Card className="border-0 shadow-sm rounded-lg h-100 bg-white">
                <Card.Body className="d-flex flex-column justify-content-between p-3">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="text-muted small text-uppercase font-weight-bold text-truncate mr-2">
                      Total a cobrar
                    </span>
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{ width: '38px', height: '38px', backgroundColor: '#FEF3C7', color: '#D97706' }}
                    >
                      <FontAwesomeIcon icon={faWallet} />
                    </div>
                  </div>
                  <div>
                    <div className="font-weight-bold mb-1 text-dark text-truncate" style={{ fontSize: '1.28rem', letterSpacing: '-0.02em' }}>
                      {currencyFormatter.format(projection.totalProjected)}
                    </div>
                    <small className="text-muted d-block text-truncate">
                      {projection.remainingInstallments} cuotas restantes
                    </small>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={4} sm={6} className="mb-3">
              <Card className="border-0 shadow-sm rounded-lg h-100 bg-white">
                <Card.Body className="d-flex flex-column justify-content-between p-3">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="text-muted small text-uppercase font-weight-bold text-truncate mr-2">
                      Total del plan
                    </span>
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{ width: '38px', height: '38px', backgroundColor: '#EEF2FF', color: '#4F46E5' }}
                    >
                      <FontAwesomeIcon icon={faCoins} />
                    </div>
                  </div>
                  <div>
                    <div className="font-weight-bold mb-1 text-dark text-truncate" style={{ fontSize: '1.28rem', letterSpacing: '-0.02em' }}>
                      {currencyFormatter.format(projection.planGrandTotal)}
                    </div>
                    <ProgressBar now={projection.collectedPercent} variant="success" className="mb-1" style={{ height: '8px' }} />
                    <small className="text-muted d-block text-truncate">
                      {projection.collectedPercent.toFixed(1)}% ya cobrado
                    </small>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <Row className="mb-2">
            <Col lg={4} sm={6} className="mb-3">
              <Card className="border-0 shadow-sm rounded-lg h-100 bg-white">
                <Card.Body className="d-flex flex-column justify-content-between p-3">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="text-muted small text-uppercase font-weight-bold text-truncate mr-2">
                      Vencido
                    </span>
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{ width: '38px', height: '38px', backgroundColor: '#FEE2E2', color: '#DC2626' }}
                    >
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                    </div>
                  </div>
                  <div>
                    <div className="font-weight-bold mb-1 text-dark text-truncate" style={{ fontSize: '1.28rem', letterSpacing: '-0.02em' }}>
                      {currencyFormatter.format(projection.overdueAmount)}
                    </div>
                    <small className="text-muted d-block text-truncate">
                      {projection.overdueInstallments} cuotas atrasadas por cobrar
                    </small>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={4} sm={6} className="mb-3">
              <Card className="border-0 shadow-sm rounded-lg h-100 bg-white">
                <Card.Body className="d-flex flex-column justify-content-between p-3">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="text-muted small text-uppercase font-weight-bold text-truncate mr-2">
                      Este mes
                    </span>
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{ width: '38px', height: '38px', backgroundColor: '#E0F2FE', color: '#0284C7' }}
                    >
                      <FontAwesomeIcon icon={faFileInvoiceDollar} />
                    </div>
                  </div>
                  <div>
                    <div className="font-weight-bold mb-1 text-dark text-truncate" style={{ fontSize: '1.28rem', letterSpacing: '-0.02em' }}>
                      {currencyFormatter.format(projection.currentMonthAmount)}
                    </div>
                    <small className="text-muted d-block text-truncate">
                      {projection.currentMonthInstallments} cuotas por cobrar en {projection.currentMonthName}
                    </small>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={4} sm={6} className="mb-3">
              <Card className="border-0 shadow-sm rounded-lg h-100 bg-white">
                <Card.Body className="d-flex flex-column justify-content-between p-3">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="text-muted small text-uppercase font-weight-bold text-truncate mr-2">
                      Alumnos
                    </span>
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{ width: '38px', height: '38px', backgroundColor: '#FEF3C7', color: '#D97706' }}
                    >
                      <FontAwesomeIcon icon={faUsers} />
                    </div>
                  </div>
                  <div>
                    <div className="font-weight-bold mb-1 text-dark" style={{ fontSize: '1.28rem', letterSpacing: '-0.02em' }}>
                      {projection.activeStudents}
                    </div>
                    <small className="text-muted d-block text-truncate">
                      {projection.studentsWithRemaining} con cuotas · {projection.studentsUpToDate} al día
                    </small>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="border-0 shadow-sm rounded-lg bg-white overflow-hidden mb-4">
            <div className="bg-light px-4 py-3 border-bottom d-flex flex-column flex-sm-row justify-content-between align-items-sm-center">
              <div className="d-flex align-items-center mb-2 mb-sm-0">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center mr-2"
                  style={{ width: '34px', height: '34px', backgroundColor: '#ECFDF5', color: '#059669' }}
                >
                  <FontAwesomeIcon icon={faChartBar} size="sm" />
                </div>
                <div>
                  <h6 className="font-weight-bold text-dark mb-0">Proyección de cobro por mes</h6>
                  <small className="text-muted">Vencido más proyección mensual de alumnos activos</small>
                </div>
              </div>
            </div>
            <Card.Body className="p-4">
              <div style={{ width: '100%', height: 380 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={projection.chartData} margin={{ top: 15, right: 30, left: 15, bottom: 20 }} barSize={44} onClick={handleBarClick} style={{ cursor: 'pointer' }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="name" stroke="#64748b" fontSize={12} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
                    <YAxis
                      width={85}
                      tickFormatter={(chartValue) => {
                        if (chartValue >= 1000000) {
                          return `$${(chartValue / 1000000).toFixed(1)}M`;
                        }
                        if (chartValue >= 1000) {
                          return `$${(chartValue / 1000).toFixed(0)}k`;
                        }
                        return `$${chartValue}`;
                      }}
                      stroke="#64748b"
                      fontSize={12}
                      tickLine={false}
                      axisLine={{ stroke: '#e2e8f0' }}
                    />
                    <RechartsTooltip content={renderProjectionTooltip} />
                    <Bar dataKey="projectedAmount" name="Proyección" radius={[4, 4, 0, 0]}>
                      {projection.chartData.map((chartEntry, chartPosition) => (
                        <Cell
                          key={chartEntry.name}
                          fill={chartPalette[chartPosition % chartPalette.length]}
                          fillOpacity={selectedBucketName && selectedBucketName !== chartEntry.name ? 0.35 : 1}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card.Body>
          </Card>

          <Card className="bg-light px-4 py-2 border rounded-lg mb-4">
            <small className="text-muted">
              Tocá una barra del gráfico para ver de qué comisiones y alumnos viene ese monto. Tocá de nuevo para cerrar.
            </small>
          </Card>

          {selectedBucketName && selectedBucketDetail && (
            <Card className="border-0 shadow-sm rounded-lg bg-white overflow-hidden mb-4">
              <div className="bg-light px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="font-weight-bold text-dark mb-0">Detalle · {selectedBucketName}</h6>
                  <small className="text-muted">Comisiones y alumnos que componen esta barra</small>
                </div>
                <Button variant="outline-secondary" size="sm" onClick={() => setSelectedBucketName(null)}>
                  <FontAwesomeIcon icon={faTimes} className="mr-1" />
                  Cerrar
                </Button>
              </div>
              <Card.Body className="p-4">
                <h6 className="font-weight-bold text-dark mb-2">Por comisión</h6>
                <div style={{ maxHeight: '260px', overflowY: 'auto' }} className="mb-4">
                  <Table striped bordered hover responsive className="mb-0">
                    <thead>
                      <tr>
                        <th>Comisión</th>
                        <th>A cobrar</th>
                        <th>Cuotas</th>
                        <th>Alumnos</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedBucketDetail.classroomRows.map((classroomRow) => (
                        <tr key={classroomRow.classroomName}>
                          <td className="font-weight-bold">{classroomRow.classroomName}</td>
                          <td className="font-weight-bold text-success">{currencyFormatter.format(classroomRow.amount)}</td>
                          <td>{classroomRow.installmentsCount}</td>
                          <td>{classroomRow.studentsCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                <h6 className="font-weight-bold text-dark mb-2">Alumnos del período</h6>
                <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
                  <Table striped bordered hover responsive className="mb-0">
                    <thead>
                      <tr>
                        <th>Alumno</th>
                        <th>Comisión</th>
                        <th>Cuotas</th>
                        <th>Monto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedBucketDetail.bucketStudents.map((bucketStudent) => (
                        <tr key={bucketStudent.identifier}>
                          <td className="font-weight-bold">{bucketStudent.fullName}</td>
                          <td>{bucketStudent.classroomName}</td>
                          <td>
                            <small className="text-muted">
                              {bucketStudent.isOverdue ? 'Vencido · ' : ''}{bucketStudent.installmentsText}
                            </small>
                          </td>
                          <td className="font-weight-bold">{currencyFormatter.format(bucketStudent.studentTotal)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          )}

          <Card className="border-0 shadow-sm rounded-lg bg-white overflow-hidden mb-4">
            <div className="bg-light px-4 py-3 border-bottom">
              <h6 className="font-weight-bold text-dark mb-0">Detalle por alumno</h6>
              <small className="text-muted">
                Ordenado por mayor total restante
                {projection.studentsWithoutPlan > 0 ? ` · ${projection.studentsWithoutPlan} activos sin plan de pagos válido` : ''}
                {projection.staleStudents > 0 ? ` · ${projection.staleStudents} excluidos por inactividad (60+ días sin pagos)` : ''}
                {projection.finishedClassrooms.length > 0 ? ` · Comisiones finalizadas: ${projection.finishedClassrooms.join(', ')}` : ''}
              </small>
            </div>
            <div style={{ maxHeight: '480px', overflowY: 'auto' }}>
              <Table striped bordered hover responsive className="mb-0">
                <thead>
                  <tr>
                    <th>Alumno</th>
                    <th>Comisión</th>
                    <th>Pagadas</th>
                    <th>Último pago</th>
                    <th>Próximo cobro</th>
                    <th>Total restante</th>
                    <th>Calendario de cuotas</th>
                  </tr>
                </thead>
                <tbody>
                  {projection.studentRows.map((studentRow) => (
                    <tr key={studentRow.identifier}>
                      <td className="font-weight-bold">{studentRow.fullName}</td>
                      <td>
                        {studentRow.classroomName}
                        {projection.finishedClassrooms.includes(studentRow.classroomName) ? (
                          <small className="text-muted d-block">Finalizada</small>
                        ) : null}
                      </td>
                      <td>{`${studentRow.paidCount} / ${studentRow.planLength}`}</td>
                      <td><small className="text-muted">{studentRow.lastBillLabel}</small></td>
                      <td>
                        {studentRow.nextLabel ? `${studentRow.nextLabel} · ${currencyFormatter.format(studentRow.nextAmount)}` : '-'}
                      </td>
                      <td className="font-weight-bold text-success">{currencyFormatter.format(studentRow.remainingTotal)}</td>
                      <td><small className="text-muted">{studentRow.scheduleText}</small></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card>
        </>
      )}
    </Container>
  );
}
